import React, { useCallback, useEffect, useState } from 'react';

import { DialogModal, MenuItem, ModalBox, Table } from 'components';
import { useIsMobile } from 'hooks';
import { Edit2, People, Trash } from 'iconsax-react';

import { InviteUserForm } from './InviteUserForm';
import { userColumns } from './MembersColumns';
import { UserRightsEditForm } from './UserRightsEditForm';

import { useSelector, useTranslation } from 'third-party';

import {
  PersisTableFilters,
  PersisTableSort,
  PersisTables,
  UserStatusRowColors,
} from 'constants/common';

import { useUser } from 'modules/user';

import { appSelectors, userSelectors } from 'selectors';

import { FilterValue } from 'components/Table/types';

import {
  extractFiltersFromSavedColumns,
  mergeSavedColumnsWithExisting,
} from 'utils/app';
import {
  getFromStorage,
  removeFromStorage,
  saveInStorage,
} from 'utils/storage';

import { APISortOrder, APIUser } from 'types/api';

import { palette } from 'styles/theme';

const DEFAULT_MEMBERS_SORT = {
  sortField: 'firstName',
  sortOrder: APISortOrder.ASC,
};

export const MembersList = () => {
  const { t } = useTranslation();

  const [selectedItem, setSelectedItem] = useState<APIUser | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const {
    loadUsers,
    loadMoreUsers,
    updateUserFilters,
    deleteUser,
    clearUserList,
    clearUserFilters,
  } = useUser();
  const isMobile = useIsMobile();

  const [loadedUserSettings, setLoadedUserSettings] = useState(false);
  const [isFilteringOn, setIsFilteringOn] = useState(false);
  const organizationId = useSelector(appSelectors.organizationId);
  const items = useSelector(userSelectors.userList);
  const userListLoaded = useSelector(userSelectors.usersListLoaded);
  const allUsersLoaded = useSelector(userSelectors.allUsersLoaded);
  const userFilters = useSelector(userSelectors.userFilters);
  const loadUsersPending = useSelector(userSelectors.loadUsersPending);
  const loadMoreUsersPending = useSelector(userSelectors.loadMoreUsersPending);
  const [columns, setColumns] = useState(userColumns(organizationId));

  useEffect(() => {
    const savedColumns = getFromStorage(
      PersisTables.Members,
      userColumns(organizationId),
    );
    const savedSort = getFromStorage(
      PersisTableSort.Members,
      DEFAULT_MEMBERS_SORT,
    );

    const columnFilters = savedColumns
      ? extractFiltersFromSavedColumns(savedColumns)
      : {};

    updateUserFilters({ ...columnFilters, searchSortOrders: [savedSort] });

    if (savedColumns) {
      setColumns(mergeSavedColumnsWithExisting(savedColumns, columns));
    }
    setIsFilteringOn(getFromStorage(PersisTableFilters.Members, false));
    setLoadedUserSettings(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  useEffect(() => {
    if (organizationId && loadedUserSettings) {
      loadUsers();
    }
  }, [userFilters, organizationId, loadedUserSettings, loadUsers]);

  useEffect(() => {
    return () => {
      clearUserList();
      clearUserFilters();
    };
  }, [clearUserFilters, clearUserList, organizationId]);

  const onColumnFilterChange = useCallback(
    (columnId: string, value: string) => {
      setColumns(prevColumns =>
        prevColumns.map(column => {
          if (column.dataId === columnId) {
            return { ...column, filter: value };
          }
          return column;
        }),
      );
      updateUserFilters({ ...userFilters, [columnId]: value });
    },
    [userFilters, updateUserFilters],
  );

  const onAdvancedFilterChange = useCallback(
    (columnId: string, value: FilterValue) => {
      setColumns(
        columns.map(column => {
          if (column.dataId === columnId) {
            return { ...column, filterValue: value };
          }
          return column;
        }),
      );

      updateUserFilters({ ...userFilters, [columnId]: value });
    },
    [userFilters, columns, updateUserFilters],
  );

  const onFilterReset = useCallback(() => {
    updateUserFilters({});
    setColumns(it =>
      it.map(column => ({
        ...column,
        filter: undefined,
        filterValue: undefined,
      })),
    );
  }, [updateUserFilters]);

  const onColumnVisibilityChanged = useCallback(
    (columnId: string, value: boolean) => {
      setColumns(it =>
        it.map(column => {
          if (column.dataId === columnId) {
            return { ...column, hidden: !value };
          }
          return column;
        }),
      );
    },
    [],
  );

  const rowOptionsMenu = useCallback(
    (item: APIUser, onRowMenuClose: () => void): React.ReactElement[] => [
      <MenuItem
        key={'delete'}
        onClick={() => {
          setSelectedItem(item);
          onRowMenuClose();
        }}
      >
        <Trash
          variant="TwoTone"
          color={palette.primary900}
          style={{ paddingRight: '0.5rem' }}
        />
        {t('membersList.removeFromOrganization')}
      </MenuItem>,
      <MenuItem
        key={'edit'}
        onClick={() => {
          setIsEditModalOpen(true);
          setSelectedItem(item);
          onRowMenuClose();
        }}
      >
        <Edit2
          variant="TwoTone"
          color={palette.primary900}
          style={{ paddingRight: '0.5rem' }}
        />
        {t('table.edit')}
      </MenuItem>,
    ],
    [t],
  );

  const onEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedItem(null);
  }, []);

  const onInviteModalClose = () => {
    setIsInviteModalOpen(false);
  };

  const onColumnHeaderClicked = useCallback(
    (columnDataId: string) => {
      const existingSortOrder = userFilters.searchSortOrders?.find(
        order => order.sortField === columnDataId,
      );

      const newSortOrder =
        existingSortOrder && existingSortOrder.sortOrder === APISortOrder.DESC
          ? APISortOrder.ASC
          : APISortOrder.DESC;

      const newSort = { sortField: columnDataId, sortOrder: newSortOrder };

      saveInStorage(PersisTableSort.Members, newSort);

      updateUserFilters({
        ...userFilters,
        searchSortOrders: [newSort],
      });
    },
    [userFilters, updateUserFilters],
  );

  return (
    <>
      <Table<APIUser>
        draggable
        header={t('membersList.header')}
        subHeader={t('membersList.subHeader')}
        headerIcon={
          <People variant="TwoTone" color={palette.primary900} size="28" />
        }
        onSaveTableSettings={settings => {
          saveInStorage(PersisTables.Members, settings);
        }}
        onResetTableSettings={() => {
          setColumns(userColumns(organizationId));
          updateUserFilters({ searchSortOrders: [DEFAULT_MEMBERS_SORT] });
          removeFromStorage(PersisTables.Members);
          removeFromStorage(PersisTableSort.Members);
        }}
        onColumnSizeChanged={(it, width) => {
          setColumns(prevColumns =>
            prevColumns.map(column =>
              column.dataId === it ? { ...column, width: width } : column,
            ),
          );
        }}
        onColumnOrderChanged={setColumns}
        filterable
        filteringOn={isFilteringOn}
        onFilteringToggle={value => {
          setIsFilteringOn(value);
          saveInStorage(PersisTableFilters.Members, value);
        }}
        allItemsLoaded={allUsersLoaded}
        infiniteScroll
        tableMinHeight="20rem"
        columns={columns}
        items={items || []}
        sortField={
          userFilters.searchSortOrders?.[0]?.sortField as keyof APIUser
        }
        sortOrder={userFilters.searchSortOrders?.[0]?.sortOrder}
        rowHighlightColor={item => UserStatusRowColors[item.userStatus]}
        rowOptionsMenu={rowOptionsMenu}
        onColumnHeaderClicked={onColumnHeaderClicked}
        onColumnFilterChange={onColumnFilterChange}
        onAdvancedFilterChange={onAdvancedFilterChange}
        onColumnVisibilityChanged={onColumnVisibilityChanged}
        loadItemsPending={loadUsersPending}
        loadMoreItemsPending={loadMoreUsersPending}
        onLoadMoreClicked={loadMoreUsers}
        onFilterReset={onFilterReset}
        bodyRowHeight="1rem"
        headerRowHeight="0.2rem"
        itemsLoaded={userListLoaded}
        onNewItemButtonClicked={() => setIsInviteModalOpen(true)}
        emptyText={t('table.empty')}
        loadMoreButtonText={t('table.loadMore')}
      />
      {isEditModalOpen && (
        <ModalBox
          width={isMobile ? '90%' : '30rem'}
          height="19rem"
          onClose={onEditModalClose}
        >
          <UserRightsEditForm
            organizationId={organizationId}
            onModalClose={onEditModalClose}
            userId={selectedItem?.id}
          />
        </ModalBox>
      )}
      {isInviteModalOpen && (
        <ModalBox
          width={isMobile ? '90%' : '30rem'}
          height="22rem"
          onClose={onInviteModalClose}
        >
          <InviteUserForm onModalClose={onInviteModalClose} />
        </ModalBox>
      )}
      <DialogModal
        onClose={() => setSelectedItem(null)}
        open={!!selectedItem && !isEditModalOpen}
        description={t('membersList.deleteConfirm')}
        handleActionClick={() => {
          if (selectedItem) {
            deleteUser(selectedItem.id);
          }
          setSelectedItem(null);
        }}
      />
    </>
  );
};
